/* The Home page: where each report has got to, against where it should be by now.
 * "By now" depends on today's month: a quarterly report should cover the last finished quarter (October -> Q3) and a monthly
 * report the last finished month (October -> September). Works on the report list (no figures needed) and the approval rows. */
import { stageLabel, statusOf, AM, FINANCE } from './approvals.js';
import { UNASSIGNED, isManagerOf, managerOf } from './constants.js';
import { MONTH_NAMES, frequencyOf, periodOf, statedFrequency } from './periods.js';

export { frequencyOf };

const monthly = (year, month) => ({ sel: { mode: 'monthly', year, month }, label: `${MONTH_NAMES[month]} ${year}`, index: year * 12 + month });
const quarterly = (year, quarter) => ({ sel: { mode: 'quarterly', year, quarter }, label: `Q${quarter + 1} ${year}`, index: year * 4 + quarter });

/* An annual report is followed through the year like a quarterly one (up to date = reported to the last finished quarter), but it is not
 * issued until January: its approvals are not asked for before then (see `awaiting`, and `issued` on each row). */
/** The last finished period for `frequency` in the month of `today` -> { sel, label, index }.
 * A quarterly report falls due only when a quarter has ended, so its period moves on in January, April, July and October
 * and stays put for the two months after; a monthly report moves on every month. */
export function expectedPeriod(frequency, today = new Date()) {
  const y = today.getFullYear(), m = today.getMonth();
  if (frequency === 'monthly') return m === 0 ? monthly(y - 1, 11) : monthly(y, m - 1);
  const q = Math.floor(m / 3) - 1;
  return q < 0 ? quarterly(y - 1, 3) : quarterly(y, q);
}

/** The period a report should have reached in `year`: the most recent reporting period for the current year, and the last
 * period of the year (December / Q4) for an earlier one. */
export function referencePeriod(frequency, today = new Date(), year = today.getFullYear()) {
  if (year >= today.getFullYear()) return expectedPeriod(frequency, today);
  return frequency === 'monthly' ? monthly(year, 11) : quarterly(year, 3);
}

/** The years offered on the Home page, newest first: every year with a report, and always the current year. */
export function homeYears(reports, today = new Date()) {
  return [...new Set([today.getFullYear(), ...reports.map((r) => r.report_year)])].sort((a, b) => b - a);
}

/** Does this report belong to the signed-in account manager? (first-name match, as for AM approval) */
export const ownedBy = (user, report) => isManagerOf(user, report);

/** The last period a report is complete for, from its as-at date (a period counts once the as-at date reaches its last day). */
function reportedPeriod(frequency, asAt) {
  const [y, mo, d] = String(asAt).split('-').map(Number);
  const endsMonth = d >= new Date(y, mo, 0).getDate();
  const month = endsMonth ? mo - 1 : mo - 2;                       // 0-11 of the last complete month (may be -1)
  const year = month < 0 ? y - 1 : y, mi = (month + 12) % 12;
  if (frequency === 'monthly') return monthly(year, mi);
  const q = Math.floor((mi + 1) / 3) - 1;                          // a quarter is complete once its last month is
  return q < 0 ? quarterly(year - 1, 3) : quarterly(year, q);
}

/** One row per report id, most overdue first, for `year` (default: the current year).
 * `config` is settlement_report_config ({ settlement_report_id, client_name, frequency }): every expected report. One with no report at all
 * is a row with state 'missing' (no report, reportedTo or approvals). Frequency: the report's own reporting_frequency (settlement_report), else the config's, else worked out from the as-at date.
 * -> { id, partner, manager, frequency, state: 'current'|'behind'|'missing', behind, reportedTo, expected, stage, approval, finance, account_manager,
 *      report, dueReport, awaiting, awaitingLevel }
 * `expected` is the period due: the most recent reporting period this year, or the last period of an earlier year.
 * stage/approval are for the period the report has reached; finance / account_manager are the approvals (or null) for the period due.
 * `awaiting` is true when the period due has been reported (or, for a report that is behind, the period it has reached has Finance approval) but is not yet approved
 * by both Finance and the Account Manager; `awaitingLevel` says who is next ('finance' or 'account_manager'); `awaitPeriod` / `awaitReport` are
 * that period and the report to open for it; `dueReport` is the report for the period due (null when behind). */
export function buildHome(reports, approvals, today = new Date(), year = today.getFullYear(), config = []) {
  const cfg = new Map((config || []).map((c) => [c.settlement_report_id, c]));
  const ids = [...new Set([...reports.map((r) => r.settlement_report_id), ...cfg.keys()])];
  const newest = (list) => list.reduce((a, b) => (!a || b.as_at_date > a.as_at_date ? b : a), null);
  const rows = ids.map((id) => {
    const mine = reports.filter((r) => r.settlement_report_id === id);
    const c = cfg.get(id);
    // the report's own reporting_frequency (reports.settlement_report) first; else the config; else worked out from the as-at date
    const frequency = statedFrequency(newest(mine)) || statedFrequency({ reporting_frequency: c && c.frequency }) || frequencyOf(newest(mine) || {});
    const expected = referencePeriod(frequency, today, year);
    if (!mine.length) {                                                          // expected, but no report has ever been received
      return { id, partner: c.client_name || '', manager: '', frequency, state: 'missing', behind: null, reportedTo: null, expected, stage: 'none', approval: '',
        finance: null, account_manager: null, report: null, dueReport: null, awaiting: false, awaitingLevel: null };
    }
    const r = newest(mine.filter((x) => x.report_year <= expected.sel.year));    // nothing yet for a report that starts after this year
    if (!r) return null;
    const reachedAt = reportedPeriod(frequency, r.as_at_date);
    const behind = Math.max(0, expected.index - reachedAt.index);
    const st = statusOf(approvals, r, periodOf(reachedAt.sel));
    const due = newest(mine.filter((x) => x.report_year === expected.sel.year));
    const dueStatus = due && !behind ? statusOf(approvals, due, periodOf(expected.sel)) : { finance: null, account_manager: null, stage: 'none' };
    // What is waiting for approval: the period due when it has been reported. When the report is behind, it is the period it has reached, but only
    // once approval of that period has started (Finance has approved): a half-done approval must not drop off the lists just because the next period
    // has fallen due. A behind report with nothing approved is not asked for (it is shown as behind on the Home page instead).
    const awaitPeriod = behind ? reachedAt : expected, awaitReport = behind ? r : due || null, awaitStatus = behind ? st : dueStatus;
    const awaiting = frequency !== 'annual' && !!awaitReport && awaitStatus.stage !== 'complete' && (!behind || awaitStatus.stage !== 'none');   // an annual report is not issued, so not awaiting approval, until January
    return { id, partner: r.client_name || r.company_name || (c && c.client_name) || '', manager: r.account_manager || '', frequency,
      state: behind ? 'behind' : 'current', behind, reportedTo: reachedAt, expected, stage: st.stage, approval: stageLabel(st.stage),
      finance: dueStatus.finance, account_manager: dueStatus.account_manager, report: r, dueReport: behind ? null : due || null,
      awaiting, awaitingLevel: awaiting ? (awaitStatus.finance ? AM : FINANCE) : null, awaitPeriod, awaitReport: awaiting ? awaitReport : null };
  }).filter(Boolean);
  const late = (x) => (x.state === 'missing' ? Infinity : x.behind);           // missing reports first, then the most overdue
  return rows.sort((a, b) => (late(b) === late(a) ? 0 : late(b) > late(a) ? 1 : -1) || a.id.localeCompare(b.id));
}

/** The Status filter shared by Home, Summary and Validation: [value, label]. '' is every status. */
export const STATUS_OPTIONS = [['current', 'Up to date'], ['behind', 'Behind'], ['missing', 'Missing'], ['', 'All statuses']];
export const DEFAULT_STATUS = 'current';

/** The Frequency filter shared by Home and Summary: [value, label]. '' is every frequency. */
export const FREQUENCY_OPTIONS = [['', 'All frequencies'], ['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['annual', 'Annual']];

/** report id -> 'monthly' | 'quarterly' | 'annual' for these Home rows. */
export const frequencyById = (rows) => new Map(rows.map((r) => [r.id, r.frequency]));

/** Does a report id pass the Frequency filter ('' = all)? */
export const passesFrequency = (byId, id, frequency) => !frequency || byId.get(id) === frequency;

/** report id -> 'current' | 'behind' | 'missing' for these Home rows. */
export const statusById = (rows) => new Map(rows.map((r) => [r.id, r.state]));

/** Does a report id pass the Status filter ('' = all)? An id Home does not list (new data with no report and no config entry) counts as missing. */
export const passesStatus = (byId, id, status) => !status || (byId.get(id) || 'missing') === status;

/** The account managers listed in these rows, exactly as they appear in the Account manager column (A-Z), with Unassigned last for reports with none. */
export function managersIn(rows) {
  const seen = new Map();
  let none = false;
  rows.forEach((r) => {
    const name = String(r.manager || '').trim().replace(/\s+/g, ' ');
    if (!name) { none = true; return; }
    if (!seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), name);
  });
  const names = [...seen.values()].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));
  return none ? [...names, UNASSIGNED] : names;
}

const nameOf = (r) => String(r.manager || '').trim().replace(/\s+/g, ' ');

/** mine: a signed-in user, to keep only the reports they are the account manager of (first-name match).
 * manager: '' (anyone) or a name from managersIn; frequency: '' (any), 'monthly', 'quarterly' or 'annual';
 * status: '' (any), 'current' (up to date), 'behind' or 'missing'. */
export const filterHome = (rows, { manager = '', frequency = '', status = '', mine = null } = {}) => rows.filter((r) =>
  (!mine || isManagerOf(mine, { account_manager: nameOf(r) })) && (!manager || (manager === UNASSIGNED ? !nameOf(r) : nameOf(r).toLowerCase() === manager.toLowerCase()))
  && (!frequency || r.frequency === frequency) && (!status || r.state === status));

/* What each sortable column sorts on. Status: up to date, then 1, 2, ... periods behind, then missing. Approvals: pending first, then by when. */
const SORT = {
  id: (r) => r.id.toLowerCase(),
  partner: (r) => (r.partner || '').toLowerCase(),
  manager: (r) => (r.manager || '').toLowerCase(),
  frequency: (r) => r.frequency,
  period: (r) => r.expected.index,
  status: (r) => (r.state === 'missing' ? Infinity : r.behind),
  finance: (r) => (r.finance ? String(r.finance.actioned_at || '~') : ''),
  account_manager: (r) => (r.account_manager ? String(r.account_manager.actioned_at || '~') : ''),
};
export const SORT_KEYS = Object.keys(SORT);

/** key: one of SORT_KEYS ('' keeps the default order: missing first, then the most overdue); dir: 'asc' | 'desc'. Ties keep the default order. */
export function sortHome(rows, key, dir = 'asc') {
  if (!SORT[key]) return rows;
  const f = SORT[key], sign = dir === 'desc' ? -1 : 1;
  return rows.map((r, i) => ({ r, v: f(r), i }))
    .sort((a, b) => (a.v === b.v ? 0 : a.v < b.v ? -sign : sign) || a.i - b.i).map((x) => x.r);
}
